import { api } from '../components/role-play/apiService';
import { Organization, OrganizationWithStats } from '../types';

interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export interface UserListItem {
  id: string;
  email: string;
  display_name: string;
  role: string;
  organization_id: string;
  created_at: string;
  user_from?: string | null;
  session_count?: number | null;
}

/** Column-level filters on the Organizations roster. Mirrors the backend's OrgFilters. */
export interface OrganizationFilters {
  /** Location contains (free text — locations are open-ended, so not a checkbox set). */
  location?: string;
  /** Any of: free, basic, pro, enterprise. Several values match any of them. */
  tier?: string[];
  /** Any of: email, in_app. Matches orgs with at least one of those channels on. */
  notifications?: string[];
  /** Purchased seat limit bounds, as raw input strings ('' means unset). */
  seatsMin?: string;
  seatsMax?: string;
}

/**
 * Write the roster's search/filter/sort state onto a URLSearchParams.
 *
 * Shared by the table fetch and the CSV export so the two can't drift — the export
 * is meant to contain exactly the rows the table is showing, and the backend pins
 * the same contract by routing both endpoints through one `_build_org_query`.
 */
export function appendOrganizationQueryParams(
  params: URLSearchParams,
  search?: string,
  sortBy?: string,
  sortOrder?: string,
  filters?: OrganizationFilters,
): URLSearchParams {
  if (search) params.set('search', search);
  if (sortBy) {
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder || 'asc');
  }
  if (filters?.location) params.set('location', filters.location);
  // Repeated keys, not a comma-joined string — FastAPI reads `List[str]` query params
  // as `?tier=free&tier=pro`, and a joined value would arrive as one unknown tier and
  // be silently dropped.
  filters?.tier?.forEach(t => params.append('tier', t));
  filters?.notifications?.forEach(n => params.append('notifications', n));
  if (filters?.seatsMin) params.set('seats_min', filters.seatsMin);
  if (filters?.seatsMax) params.set('seats_max', filters.seatsMax);
  return params;
}

export async function fetchOrganizations(
  page: number,
  pageSize: number,
  search?: string,
  sortBy?: string,
  sortOrder?: string,
  filters?: OrganizationFilters,
): Promise<PaginatedResponse<Organization>> {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  appendOrganizationQueryParams(params, search, sortBy, sortOrder, filters);
  return api.get<PaginatedResponse<Organization>>(`/organization?${params.toString()}`);
}

// Org name/details rarely change and this is fetched independently by many components
// (assignment list/detail/edit, OrgSelect, various content editor views) — cache in-flight
// and resolved requests per org id for the session so navigating between them doesn't
// refetch the same org repeatedly. Invalidated on update/delete below.
const orgByIdCache = new Map<string, Promise<Organization>>();

export async function fetchOrganizationById(orgId: string): Promise<Organization> {
  let cached = orgByIdCache.get(orgId);
  if (!cached) {
    cached = api.get<Organization>(`/organization/${orgId}`);
    cached.catch(() => orgByIdCache.delete(orgId)); // don't cache failures
    orgByIdCache.set(orgId, cached);
  }
  return cached;
}

// Same endpoint as fetchOrganizationById, deliberately bypassing the cache above.
// seat_limit changes the moment seats are purchased, and nothing invalidates the
// session-long orgByIdCache on a purchase, so a caller rendering it must not be served a
// stale copy — an admin who has just bought seats would otherwise keep seeing the old limit
// for the rest of their session.
//
// Note this protects the *limit* only. HomePage's Seats Used tile takes its numerator from
// /gamification/roster, which has its own 5-minute Redis cache, so bypassing this one does
// not make that tile fully live — see the comment on the tile.
export async function fetchOrganizationUncached(orgId: string): Promise<Organization> {
  return api.get<Organization>(`/organization/${orgId}`);
}

/**
 * Org detail plus its aggregate stats, optionally within a date range.
 *
 * The dates are the same ISO calendar-date strings the analytics dashboard sends, and
 * the endpoint normalizes them the same way, so the same window asked of either place
 * answers with the same numbers.
 */
export async function fetchOrganizationWithStats(
  orgId: string,
  range?: { startDate?: string; endDate?: string },
): Promise<OrganizationWithStats> {
  const params = new URLSearchParams();
  if (range?.startDate) params.set('startDate', range.startDate);
  if (range?.endDate) params.set('endDate', range.endDate);
  const query = params.toString();
  return api.get<OrganizationWithStats>(`/organization/${orgId}/stats${query ? `?${query}` : ''}`);
}

export async function fetchOrganizationUsers(
  orgId: string,
  page: number,
  pageSize: number,
  search?: string
): Promise<PaginatedResponse<UserListItem>> {
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
  return api.get<PaginatedResponse<UserListItem>>(
    `/organization/${orgId}/users?page=${page}&page_size=${pageSize}${searchParam}`
  );
}

export async function createOrganization(data: Partial<Organization>): Promise<Organization> {
  return api.post<Organization>('/organization', data);
}

export async function updateOrganization(id: string, data: Partial<Organization>): Promise<Organization> {
  const updated = await api.put<Organization>(`/organization/${id}`, data);
  orgByIdCache.delete(id);
  return updated;
}

export async function deleteOrganization(id: string): Promise<void> {
  await api.delete(`/organization/${id}`);
  orgByIdCache.delete(id);
}
